// ./src/utils/api/AppFeatureCreditService.ts
//
// PURCHASED credits — the top-up wallet, as distinct from the monthly allowance.
//
// ⚠ CREDITS AND ALLOWANCE ARE DIFFERENT FEATURES WITH DIFFERENT CODES, counted in different
// tables, and this is the single most confusable thing on this rail. `email_qty` is the monthly
// allowance (`app_feature_usage`, resets every month, expires). `email_credits` is what you buy
// (`app_feature_credit`, never expires, spent FIRST). Read the pairing from `FEATURE_TOP_UPS`
// in the generated feature codes — never by string-munging `_qty` into `_credits`.
//
// ⚠ ACCOUNT SCOPE ONLY — the server returns nothing but `scopeRef: ''`, and since A2.9.2 that
// is every credit an account can have. This used to be an EXCLUSION worth arguing about:
// credits bought from an event dashboard were locked to one event, and folding them into a
// figure labelled "purchased" would have overstated what any given send could draw on. Top-ups
// are account-wide everywhere now, so this read is complete rather than filtered.
//
// ⚠ The `scopeRef` field below is still SELECTED and still part of the shape. Do not drop it:
// ms_auth's credit table is scope-keyed by design and the server's spend order still reads an
// event tier (A9.4 is its future writer). The field being uniformly `''` today is a fact about
// the data, not a reason to stop asking for it.
//
// ⚠ This op lives in ms_auth and is `doAuth: true`, scoped to the CALLER's own account from the
// token. It takes NO arguments at all — verified against the live schema, which rejects an
// `accountId` argument outright — so there is nothing to spoof. Its interservice sibling
// `appFeatureCreditBalance` DOES take an accountId and is gated on the interservice key; it must
// never be called from a browser.
//
// ⚠ A feature with no balance row is ABSENT, not zero — it has simply never been topped up.
// Absent-after-a-successful-read is a true zero; absent because the read FAILED is unknown, and
// telling a host they have no credits when they have 29,899 is the failure mode this whole file
// has to avoid. `useAppFeatureCredits` keeps the two apart with a `loaded` flag.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const APP_FEATURE_CREDITS_MINE_QUERY = gql`
  query AppFeatureCreditsMine {
    appFeatureCreditsMine {
      code
      data {
        featureCode
        featureName
        scopeRef
        granted
        consumed
        remaining
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface AppFeatureCreditRow {
  featureCode?: string | null;
  featureName?: string | null;
  /** Always '' from this op — account scope. Kept so the shape matches the server's. */
  scopeRef?: string | null;
  /** Total ever bought. Only goes up; a refund claws it back toward `consumed`. */
  granted?: number | null;
  /** Total ever spent. Only goes up — a consumed credit can never be un-consumed. */
  consumed?: number | null;
  /** ⚠ THE SPENDABLE NUMBER. `granted - consumed`, floored at 0. Show this one. */
  remaining?: number | null;
}

export class AppFeatureCreditService {
  /** Every account-wide credit balance the signed-in host holds. */
  static async mine(): Promise<OpResult> {
    return ApiService.executeQuery(APP_FEATURE_CREDITS_MINE_QUERY, {});
  }
}
